import { Injectable, Optional, Inject } from '@angular/core';
import { Subject } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { SiteService } from './site.service';
import { PlatformService } from './platform.service';

const CONSENT_KEY = 'lp_cookie_consent';

// Global fallback GA4 measurement ID used ONLY for sites that don't have
// their own per-tenant GA4 property configured in site.ext.ga4MeasurementId.
// Once every client site has its own property provisioned, this can go away.
const GLOBAL_FALLBACK_ANALYTICS_ID = 'G-ZP93MBFNEL';

interface ConsentRecord {
  analytics: boolean;
  timestamp: string;
  version: string;
}

@Injectable({ providedIn: 'root' })
export class CookieConsentService {
  private readonly POLICY_VERSION = '1.0';
  private openPreferencesSubject = new Subject<void>();
  readonly openPreferences$ = this.openPreferencesSubject.asObservable();

  private openPrivacySubject = new Subject<void>();
  readonly openPrivacy$ = this.openPrivacySubject.asObservable();

  private openTermsSubject = new Subject<void>();
  readonly openTerms$ = this.openTermsSubject.asObservable();

  /**
   * Track whether we've already loaded the gtag script for a given measurement ID
   * so we don't double-load if site context arrives after consent.
   */
  private loadedMeasurementId: string | null = null;

  constructor(
    @Optional() @Inject(SiteService) private siteService: SiteService | null,
    private platformService: PlatformService,
  ) {}

  openPreferences(): void {
    this.openPreferencesSubject.next();
  }

  openPrivacyPolicy(): void {
    this.openPrivacySubject.next();
  }

  openTermsOfUse(): void {
    this.openTermsSubject.next();
  }

  getConsent(): ConsentRecord | null {
    try {
      const raw = localStorage.getItem(CONSENT_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  hasDecided(): boolean {
    return this.getConsent() !== null;
  }

  hasAnalyticsConsent(): boolean {
    return this.getConsent()?.analytics === true;
  }

  accept(): void {
    this.saveConsent(true);
    this.loadAnalytics();
  }

  decline(): void {
    this.saveConsent(false);
    // Also silence an already-loaded gtag on this page (opt-out on notice-mode
    // sites, or withdraw-consent on GDPR sites) — future page loads won't load
    // it at all because initFromStorage checks the stored record.
    const win = window as any;
    if (win.gtag) {
      win.gtag('consent', 'update', { analytics_storage: 'denied' });
    }
  }

  /**
   * Called on app init. Two code paths:
   *
   * GDPR sites (enableGdpr: true): analytics only fires after the user
   * explicitly accepts the cookie consent banner. This method loads it
   * immediately if a prior acceptance is stored in localStorage.
   *
   * Non-GDPR sites (enableGdpr: false): no consent banner is ever shown,
   * so localStorage never has a consent record. Analytics must fire
   * automatically once the site config is known.
   */
  initFromStorage(): void {
    // Analytics scripts and consent storage are entirely a browser concern —
    // nothing here should run (or can safely run — no localStorage/document)
    // while rendering on the server.
    if (!this.platformService.isBrowser) return;
    if (this.hasAnalyticsConsent()) {
      // Returning visitor on any site who already accepted — load immediately.
      this.loadAnalytics();
      return;
    }

    // An explicit "no" (GDPR decline or notice-mode opt-out) is honored on
    // every site type — never auto-fire over it.
    if (this.getConsent()?.analytics === false) {
      return;
    }

    // Non-GDPR sites: auto-fire analytics without waiting for user consent.
    // We must wait for the site to load before we know whether GDPR is enabled.
    if (this.siteService) {
      this.siteService.site$.pipe(
        filter(s => s !== null),
        take(1),
      ).subscribe(site => {
        const ext = (site as any)?.ext as any;
        if (!ext?.enableGdpr) {
          this.loadAnalytics();
        }
        // GDPR sites: do nothing here — the cookie banner's accept() handles it.
      });
    }
  }

  private saveConsent(analytics: boolean): void {
    const record: ConsentRecord = {
      analytics,
      timestamp: new Date().toISOString(),
      version: this.POLICY_VERSION
    };
    localStorage.setItem(CONSENT_KEY, JSON.stringify(record));
  }

  /**
   * Resolve which GA4 measurement ID to fire for the current tenant.
   * Order of precedence:
   *   1. site.ext.ga4MeasurementId (per-tenant property — preferred)
   *   2. GLOBAL_FALLBACK_ANALYTICS_ID (only until every client site has its own)
   */
  private resolveMeasurementId(): string | null {
    const site = this.siteService?.getCurrentSite();
    const perTenant = (site?.ext as any)?.ga4MeasurementId;
    if (perTenant && typeof perTenant === 'string' && perTenant.startsWith('G-')) {
      return perTenant;
    }
    return GLOBAL_FALLBACK_ANALYTICS_ID || null;
  }

  private loadAnalytics(): void {
    // If site not yet loaded, wait for it once and then load with per-tenant ID
    const site = this.siteService?.getCurrentSite();
    if (!site && this.siteService) {
      this.siteService.site$.pipe(take(2)).subscribe(s => {
        if (s) this.fireGtag(this.resolveMeasurementId());
      });
      return;
    }
    this.fireGtag(this.resolveMeasurementId());
  }

  private fireGtag(measurementId: string | null): void {
    if (!measurementId) return; // No tag configured for this tenant and no global fallback
    if (this.loadedMeasurementId === measurementId) return; // Already loaded
    if (document.getElementById('lp-gtag-script')) return;  // Belt + suspenders

    const win = window as any;
    win.dataLayer = win.dataLayer || [];
    // gtag stub must use `arguments` (not rest params) — gtag.js reads the Arguments object
    if (!win.gtag) {
      win.gtag = function() { win.dataLayer.push(arguments); };
    }

    // Grant consent BEFORE appending the script so gtag.js sees it in the
    // dataLayer queue on first read. Calling this inside onload is too late —
    // gtag.js has already started its wait_for_update timer by then.
    win.gtag('consent', 'update', { analytics_storage: 'granted', ad_storage: 'denied' });

    const script = document.createElement('script');
    script.id = 'lp-gtag-script';
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    document.head.appendChild(script);

    script.onload = () => {
      // gtag.js is now initialised — use its own gtag function for js + config
      win.gtag('js', new Date());
      win.gtag('config', measurementId);
      this.loadedMeasurementId = measurementId;
    };
  }
}
